import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Upload, Save, GripVertical, ImagePlus, FileText, X, Megaphone, Share2 } from "lucide-react";
import { toast } from "sonner";
import { downscaleImage, fileToDataUrl, FIELD_TYPES, resizeBannerToOg } from "@/lib/eventLib";
import PassTemplateEditor from "@/components/events/PassTemplateEditor";
import { ContactNumbersInput } from "@/components/events/ContactNumbers";

const blank = {
  name: "", description: "", banner: "", pics: [], document: "", document_name: "",
  contact_number: "", city: "", event_date: "", event_time: "",
  registration_open: true, closed_message: "stay_tuned",
  payment_required: false, payment_number: "", payment_qr: "", payment_note: "",
  payment_categories: [], fields: [], pass_template: {},
  announcement_text: "", announcement_image: "", announcement_doc: "", announcement_doc_name: "",
  social_links: [], videos: [],
};

const uid = () => "f_" + Math.random().toString(36).slice(2, 8);

export default function EventEditor({ eventId, onDone }) {
  const [ev, setEv] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const bannerRef = useRef(null);
  const picsRef = useRef(null);
  const docRef = useRef(null);
  const qrRef = useRef(null);
  const annImgRef = useRef(null);
  const annDocRef = useRef(null);

  useEffect(() => {
    if (eventId) api.get(`/events/${eventId}`).then((r) => setEv({ ...blank, ...r.data })).catch(() => toast.error("Load failed"));
  }, [eventId]);

  const up = (patch) => setEv((e) => ({ ...e, ...patch }));

  const onImg = async (file, key) => {
    if (!file) return;
    try { up({ [key]: key === "banner" ? await resizeBannerToOg(file) : await downscaleImage(file, 900) }); }
    catch { toast.error("Image load failed"); }
  };
  const onPics = async (files) => {
    const arr = [];
    for (const f of Array.from(files).slice(0, 8)) { try { arr.push(await downscaleImage(f, 1000)); } catch { /* skip */ } }
    up({ pics: [...ev.pics, ...arr].slice(0, 8) });
  };
  const onDoc = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Document 5 MB se bada nahi ho sakta");
    up({ document: await fileToDataUrl(file), document_name: file.name });
  };
  const onAnnDoc = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File 5 MB se bada nahi ho sakti");
    up({ announcement_doc: await fileToDataUrl(file), announcement_doc_name: file.name });
  };

  const addField = () => up({ fields: [...ev.fields, { key: uid(), label: "", type: "text", required: false, options: [] }] });
  const updSocial = (i, p) => up({ social_links: (ev.social_links || []).map((s, j) => (j === i ? { ...s, ...p } : s)) });
  const addSocial = () => up({ social_links: [...(ev.social_links || []), { type: "facebook", url: "" }] });
  const delSocial = (i) => up({ social_links: (ev.social_links || []).filter((_, j) => j !== i) });
  const updVideo = (i, p) => up({ videos: (ev.videos || []).map((v, j) => (j === i ? { ...v, ...p } : v)) });
  const addVideo = () => up({ videos: [...(ev.videos || []), { text: "", url: "" }] });
  const delVideo = (i) => up({ videos: (ev.videos || []).filter((_, j) => j !== i) });
  const updField = (i, patch) => up({ fields: ev.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });
  const delField = (i) => up({ fields: ev.fields.filter((_, idx) => idx !== i) });

  const addCat = () => up({ payment_categories: [...ev.payment_categories, { id: uid(), name: "", amount: 0 }] });
  const updCat = (i, patch) => up({ payment_categories: ev.payment_categories.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const delCat = (i) => up({ payment_categories: ev.payment_categories.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!ev.name.trim()) return toast.error("Event ka naam daalein");
    if (ev.fields.some((f) => !f.label.trim())) return toast.error("Har field ka label daalein");
    setBusy(true);
    try {
      const payload = {
        ...ev,
        fields: ev.fields.map((f) => ({ ...f, options: (f.type === "dropdown" || f.type === "checkbox") ? (Array.isArray(f.options) ? f.options : String(f.options).split(",").map((o) => o.trim()).filter(Boolean)) : [] })),
      };
      if (eventId) await api.put(`/events/${eventId}`, payload);
      else await api.post("/events", payload);
      toast.success("Event saved");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="event-editor">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex"><div className="flex-1 bg-cyan-500" /><div className="flex-1 bg-pink-600" /><div className="flex-1 bg-yellow-400" /><div className="flex-1 bg-slate-900" /></div>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={onDone} className="gap-1 -ml-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
          <p className="font-heading font-bold flex-1">{eventId ? "Edit Event" : "New Event"}</p>
          <Button size="sm" onClick={save} disabled={busy} data-testid="event-save-btn" className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white"><Save className="h-4 w-4" /> {busy ? "Saving..." : "Save"}</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <p className="font-heading font-semibold text-sm">Event Details</p>
          <div className="space-y-1.5"><Label>Event Name *</Label><Input value={ev.name} onChange={(e) => up({ name: e.target.value })} data-testid="event-name-input" placeholder="e.g. Annual Sports Meet 2026" /></div>
          <div className="space-y-1.5"><Label>Short Description</Label><textarea value={ev.description} onChange={(e) => up({ description: e.target.value })} data-testid="event-desc-input" rows={3} className="w-full text-sm border border-input rounded-md px-3 py-2 resize-y" placeholder="Event ke bare me kuch lines..." /></div>
          <div className="space-y-1.5"><Label>Contact Numbers (query ke liye candidate ko dikhenge)</Label><ContactNumbersInput value={ev.contact_number} onChange={(v) => up({ contact_number: v })} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Event City (optional)</Label><Input value={ev.city} onChange={(e) => up({ city: e.target.value })} data-testid="event-city-input" placeholder="e.g. Delhi" /></div>
            <div className="space-y-1.5"><Label>Event Start Date (Optional)</Label><Input type="date" value={ev.event_date} onChange={(e) => up({ event_date: e.target.value })} data-testid="event-date-input" /></div>
            <div className="space-y-1.5"><Label>Event Time (optional)</Label><Input type="time" value={ev.event_time} onChange={(e) => up({ event_time: e.target.value })} data-testid="event-time-input" /></div>
          </div>

          <div className="space-y-2 pt-3 border-t">
            <label className="flex items-center gap-2 font-heading font-semibold text-sm cursor-pointer">
              <input type="checkbox" checked={ev.registration_open} onChange={(e) => up({ registration_open: e.target.checked })} data-testid="event-reg-toggle" /> Registration Open (candidate ko form + payment dikhega)
            </label>
            {!ev.registration_open && (
              <div className="space-y-1.5 pl-1" data-testid="event-reg-msg">
                <Label>Band hone par candidate ko kya dikhe:</Label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="closed_msg" checked={ev.closed_message !== "closed"} onChange={() => up({ closed_message: "stay_tuned" })} data-testid="event-reg-msg-stay" /> Stay Tuned — Registrations Will Open Soon!
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="closed_msg" checked={ev.closed_message === "closed"} onChange={() => up({ closed_message: "closed" })} data-testid="event-reg-msg-closed" /> Registrations Are Currently Closed.
                </label>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Banner (fixed size)</Label>
            <input type="file" accept="image/*" hidden ref={bannerRef} onChange={(e) => { onImg(e.target.files[0], "banner"); e.target.value = ""; }} />
            {ev.banner ? (
              <div className="relative"><img src={ev.banner} alt="banner" className="w-full aspect-[1200/630] object-cover rounded-lg border" /><button onClick={() => up({ banner: "" })} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 text-red-500"><X className="h-4 w-4" /></button></div>
            ) : (
              <Button variant="outline" onClick={() => bannerRef.current?.click()} data-testid="event-banner-btn" className="gap-1.5 w-full h-24 border-dashed"><ImagePlus className="h-5 w-5" /> Upload Banner</Button>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Event Photos (max 8)</Label>
            <input type="file" accept="image/*" multiple hidden ref={picsRef} onChange={(e) => { onPics(e.target.files); e.target.value = ""; }} />
            <div className="flex flex-wrap gap-2">
              {ev.pics.map((p, i) => (
                <div key={i} className="relative"><img src={p} alt="" onClick={() => setZoomImg(p)} className="h-16 w-16 object-cover rounded border cursor-zoom-in hover:opacity-90 transition-opacity" data-testid={`event-pic-thumb-${i}`} /><button onClick={() => up({ pics: ev.pics.filter((_, idx) => idx !== i) })} className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 text-red-500 border"><X className="h-3 w-3" /></button></div>
              ))}
              {ev.pics.length < 8 && <Button variant="outline" onClick={() => picsRef.current?.click()} data-testid="event-pics-btn" className="h-16 w-16 border-dashed"><Plus className="h-5 w-5" /></Button>}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Document (PDF/Image, optional)</Label>
            <input type="file" hidden ref={docRef} onChange={(e) => { onDoc(e.target.files[0]); e.target.value = ""; }} />
            {ev.document ? (
              <div className="flex items-center gap-2 text-sm bg-slate-50 border rounded-lg px-3 py-2"><FileText className="h-4 w-4 text-cyan-600" /><span className="flex-1 truncate">{ev.document_name || "Document"}</span><button onClick={() => up({ document: "", document_name: "" })} className="text-red-500"><X className="h-4 w-4" /></button></div>
            ) : (
              <Button variant="outline" onClick={() => docRef.current?.click()} data-testid="event-doc-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Upload Document</Button>
            )}
          </div>
        </section>

        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-amber-600" /> Announcement (optional)</p>
          <p className="text-xs text-muted-foreground">Candidate ko form par dikhne wala notice — short text, image ya PDF. Khaali chhodenge to kuch nahi dikhega.</p>
          <div className="space-y-1.5"><Label>Announcement Text</Label><textarea value={ev.announcement_text} onChange={(e) => up({ announcement_text: e.target.value })} data-testid="event-ann-text" rows={3} maxLength={2000} className="w-full text-sm border border-input rounded-md px-3 py-2 resize-y" placeholder="e.g. Reporting time 9 AM. ID card zaroor laayein." /></div>
          <div className="space-y-1.5">
            <Label>Announcement Image (optional)</Label>
            <input type="file" accept="image/*" hidden ref={annImgRef} onChange={(e) => { onImg(e.target.files[0], "announcement_image"); e.target.value = ""; }} />
            {ev.announcement_image ? (
              <div className="relative w-fit"><img src={ev.announcement_image} alt="announcement" onClick={() => setZoomImg(ev.announcement_image)} className="max-h-40 rounded-lg border cursor-zoom-in" data-testid="event-ann-image-preview" /><button onClick={() => up({ announcement_image: "" })} className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 text-red-500 border"><X className="h-3.5 w-3.5" /></button></div>
            ) : (
              <Button variant="outline" onClick={() => annImgRef.current?.click()} data-testid="event-ann-image-btn" className="gap-1.5"><ImagePlus className="h-4 w-4" /> Upload Image</Button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Announcement PDF / File (optional)</Label>
            <input type="file" hidden ref={annDocRef} onChange={(e) => { onAnnDoc(e.target.files[0]); e.target.value = ""; }} />
            {ev.announcement_doc ? (
              <div className="flex items-center gap-2 text-sm bg-slate-50 border rounded-lg px-3 py-2"><FileText className="h-4 w-4 text-amber-600" /><span className="flex-1 truncate">{ev.announcement_doc_name || "File"}</span><button onClick={() => up({ announcement_doc: "", announcement_doc_name: "" })} className="text-red-500"><X className="h-4 w-4" /></button></div>
            ) : (
              <Button variant="outline" onClick={() => annDocRef.current?.click()} data-testid="event-ann-doc-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Upload PDF/File</Button>
            )}
          </div>
        </section>

        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!ev.pass_download_enabled} onChange={(e) => up({ pass_download_enabled: e.target.checked })} data-testid="event-passdl-toggle" className="h-4 w-4" />
            <span className="font-heading font-semibold text-sm">Pass Download (Candidate Pass Key)</span>
          </label>
          <p className="text-xs text-muted-foreground">On karne par candidate apni ek 4/6 digit pass key banayega aur baad me pass dekh/download kar sakega. Niche wo field chunein jisse candidate apna pass dhoondhega.</p>
          {ev.pass_download_enabled && (
            <div className="space-y-1.5">
              <Label>Pass dhoondhne wala field</Label>
              <select value={ev.pass_key_field || ""} onChange={(e) => up({ pass_key_field: e.target.value })} data-testid="event-passdl-field" className="w-full h-9 text-sm border border-input rounded-md px-2 bg-white">
                <option value="">Select field</option>
                {(ev.fields || []).filter((f) => f.type !== "photo").map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
          )}
        </section>

        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <p className="font-heading font-semibold text-sm flex items-center gap-1.5"><Share2 className="h-4 w-4 text-sky-600" /> Social Links &amp; Videos (optional)</p>
          <p className="text-xs text-muted-foreground">Candidate event kholne par ye links/videos dekh kar click karke khol sakega.</p>
          <div className="space-y-2">
            {(ev.social_links || []).map((s, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`event-social-row-${i}`}>
                <select value={s.type} onChange={(e) => updSocial(i, { type: e.target.value })} className="h-9 text-sm border border-input rounded-md px-2 bg-white" data-testid={`event-social-type-${i}`}>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube">YouTube</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="other">Other</option>
                </select>
                <Input value={s.url} onChange={(e) => updSocial(i, { url: e.target.value })} placeholder="https://..." data-testid={`event-social-url-${i}`} className="text-sm flex-1" />
                <Button variant="ghost" size="icon" onClick={() => delSocial(i)} className="text-red-500 h-9 w-9" data-testid={`event-social-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSocial} data-testid="event-social-add-btn" className="gap-1"><Plus className="h-4 w-4" /> Social link add karo</Button>
          </div>
          <div className="space-y-2 pt-2 border-t border-border">
            <Label>Videos (link + upar dikhne wala short text)</Label>
            {(ev.videos || []).map((v, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`event-video-row-${i}`}>
                <Input value={v.text} onChange={(e) => updVideo(i, { text: e.target.value })} placeholder="Short text (e.g. Last year highlights)" className="text-sm w-2/5" data-testid={`event-video-text-${i}`} />
                <Input value={v.url} onChange={(e) => updVideo(i, { url: e.target.value })} placeholder="Video link (YouTube/...)" className="text-sm flex-1" data-testid={`event-video-url-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => delVideo(i)} className="text-red-500 h-9 w-9" data-testid={`event-video-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addVideo} data-testid="event-video-add-btn" className="gap-1"><Plus className="h-4 w-4" /> Video add karo</Button>
          </div>
        </section>

        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-heading font-semibold text-sm">Form Fields</p>
            <Button size="sm" variant="outline" onClick={addField} data-testid="event-add-field-btn" className="gap-1 h-8"><Plus className="h-3.5 w-3.5" /> Add Field</Button>
          </div>
          <p className="text-xs text-muted-foreground">Candidate ke liye registration form fields yaha banayein.</p>
          {ev.fields.map((f, i) => (
            <div key={f.key} className="border border-border rounded-lg p-3 space-y-2" data-testid={`event-field-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-slate-300" />
                <Input value={f.label} onChange={(e) => updField(i, { label: e.target.value })} placeholder="Field label (e.g. Full Name)" data-testid={`event-field-label-${i}`} className="flex-1" />
                <Select value={f.type} onValueChange={(v) => updField(i, { type: v })}>
                  <SelectTrigger className="w-36" data-testid={`event-field-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => delField(i)} className="text-red-500" data-testid={`event-field-del-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              {(f.type === "dropdown" || f.type === "checkbox") && (
                <Input value={Array.isArray(f.options) ? f.options.join(", ") : f.options} onChange={(e) => updField(i, { options: e.target.value.split(",").map((o) => o.trim()) })} placeholder="Options comma se: Small, Medium, Large" data-testid={`event-field-options-${i}`} className="text-sm" />
              )}
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={f.required} onChange={(e) => updField(i, { required: e.target.checked })} data-testid={`event-field-required-${i}`} /> Required (bharna zaroori)
              </label>
            </div>
          ))}
        </section>

        <section className="bg-card border border-border rounded-xl p-4 space-y-3">
          <label className="flex items-center gap-2 font-heading font-semibold text-sm cursor-pointer">
            <input type="checkbox" checked={ev.payment_required} onChange={(e) => up({ payment_required: e.target.checked })} data-testid="event-payment-toggle" /> Payment Required ?
          </label>
          {ev.payment_required && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Payment Number / UPI</Label><Input value={ev.payment_number} onChange={(e) => up({ payment_number: e.target.value })} data-testid="event-payment-number" placeholder="Payment No." /></div>
              <div className="space-y-1.5">
                <Label>Payment QR</Label>
                <input type="file" accept="image/*" hidden ref={qrRef} onChange={(e) => { onImg(e.target.files[0], "payment_qr"); e.target.value = ""; }} />
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => qrRef.current?.click()} data-testid="event-qr-btn" className="gap-1.5 h-9"><Upload className="h-4 w-4" /> Upload QR</Button>
                  {ev.payment_qr && <div className="relative"><img src={ev.payment_qr} alt="qr" className="h-12 w-12 rounded border" /><button onClick={() => up({ payment_qr: "" })} className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-0.5 text-red-500 border"><X className="h-3 w-3" /></button></div>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Payment Note (optional)</Label>
                <textarea value={ev.payment_note} onChange={(e) => up({ payment_note: e.target.value })} data-testid="event-payment-note" rows={2} maxLength={500} className="w-full text-sm border border-input rounded-md px-3 py-2 resize-y" placeholder="e.g. QR scan karke pay karein, phir screenshot upload karein" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label>Payment Categories</Label><Button size="sm" variant="outline" onClick={addCat} data-testid="event-add-cat-btn" className="h-7 text-xs gap-1"><Plus className="h-3 w-3" /> Add</Button></div>
                {ev.payment_categories.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-2" data-testid={`event-cat-${i}`}>
                    <Input value={c.name} onChange={(e) => updCat(i, { name: e.target.value })} placeholder="Category (e.g. Single)" className="flex-1" data-testid={`event-cat-name-${i}`} />
                    <Input type="number" value={c.amount} onChange={(e) => updCat(i, { amount: Number(e.target.value) })} placeholder="Amount" className="w-28" data-testid={`event-cat-amount-${i}`} />
                    <button onClick={() => delCat(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <PassTemplateEditor template={ev.pass_template} onChange={(tpl) => up({ pass_template: tpl })} fields={ev.fields} eventName={ev.name} serialPrefix={ev.serial_prefix} />
      </main>

      {zoomImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" data-testid="editor-zoom-overlay" onClick={() => setZoomImg(null)}>
          <button onClick={() => setZoomImg(null)} className="absolute top-4 right-4 bg-white/15 hover:bg-white/25 text-white rounded-full p-2.5" data-testid="editor-zoom-close"><X className="h-5 w-5" /></button>
          <img src={zoomImg} alt="" onClick={(e) => e.stopPropagation()} className="max-w-full max-h-[85vh] rounded-lg" data-testid="editor-zoom-img" />
        </div>
      )}
    </div>
  );
}
